import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  ClipboardList,
  Plus,
  CheckCircle,
  Trash2,
  ChevronUp,
  ChevronDown,
  Workflow,
  Pencil,
  X,
  Check,
} from 'lucide-react';
import { format, isPast } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventTask, EventTaskStatus, EventTeamMember } from '../../types/eventTeam';
import { EVENT_TASK_STATUSES } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { EventTaskStatusBadge } from './EventTaskStatusBadge';
import { ApplyWorkflowModal } from './ApplyWorkflowModal';
import { PlayerProfile } from '../PlayerProfile';
import { cn } from '../../utils/cn';
import { EmptyState } from '../ui/EmptyState';

export interface EventTasksSectionProps {
  eventId: string;
  scheduledStart: number | null;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

function msToLocalInput(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToMs(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Run-sheet: create/edit/complete/delete/reorder tasks + apply workflow (guide §1.4). */
export const EventTasksSection = ({
  eventId,
  scheduledStart,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventTasksSectionProps) => {
  const [tasks, setTasks] = useState<EventTask[]>([]);
  const [statusFilter, setStatusFilter] = useState<EventTaskStatus | ''>('');
  const [members, setMembers] = useState<EventTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Creation form
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [newDue, setNewDue] = useState('');
  const [creating, setCreating] = useState(false);

  // Inline editor
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAssignee, setEditAssignee] = useState('');
  const [editDue, setEditDue] = useState('');

  const [showApplyWorkflow, setShowApplyWorkflow] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [tasksRes, membersRes] = await Promise.all([
        eventTeamApiService.getEventTasks(eventId, statusFilter),
        eventTeamApiService.listTeamMembers({ status: 'active', limit: 200 }),
      ]);
      setTasks([...tasksRes].sort((a, b) => a.position - b.position));
      setMembers(membersRes.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [eventId, statusFilter]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const maxPos = tasks.reduce((max, t) => Math.max(max, t.position), 0);
      await eventTeamApiService.createTask(eventId, {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        assignee_uuid: newAssignee || null,
        due_date: localInputToMs(newDue),
        position: maxPos + 1,
      });
      setNewTitle('');
      setNewDescription('');
      setNewAssignee('');
      setNewDue('');
      setShowCreate(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (task: EventTask) => {
    setEditingId(task.id);
    setEditTitle(task.title);
    setEditDescription(task.description ?? '');
    setEditAssignee(task.assignee_uuid ?? '');
    setEditDue(msToLocalInput(task.due_date));
  };

  const handleSaveEdit = async (task: EventTask) => {
    setBusyId(task.id);
    try {
      await eventTeamApiService.updateTask(task.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        assignee_uuid: editAssignee || null,
        due_date: localInputToMs(editDue),
      });
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update task');
    } finally {
      setBusyId(null);
    }
  };

  const handleStatusChange = async (task: EventTask, status: EventTaskStatus) => {
    setBusyId(task.id);
    try {
      await eventTeamApiService.updateTask(task.id, { status });
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update task status');
    } finally {
      setBusyId(null);
    }
  };

  const handleComplete = async (task: EventTask) => {
    setBusyId(task.id);
    try {
      await eventTeamApiService.completeTask(task.id);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to complete task');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (task: EventTask) => {
    if (!confirm(`Delete task "${task.title}"?`)) return;
    setBusyId(task.id);
    try {
      await eventTeamApiService.deleteTask(task.id);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete task');
    } finally {
      setBusyId(null);
    }
  };

  /** Swap position with the neighbour via fractional PATCH (only meaningful without a status filter). */
  const handleMove = async (task: EventTask, direction: -1 | 1) => {
    const idx = tasks.findIndex((t) => t.id === task.id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= tasks.length) return;
    const neighbour = tasks[targetIdx];
    const beyond = tasks[targetIdx + direction];
    const newPos = beyond ? (neighbour.position + beyond.position) / 2 : neighbour.position + direction;
    setBusyId(task.id);
    try {
      await eventTeamApiService.updateTask(task.id, { position: newPos });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reorder task');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-accent-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-amber-400" />
          Run-sheet ({tasks.length})
        </h3>
        <div className="flex items-center gap-2">
          {perms.canLead && (
            <button
              onClick={() => setShowApplyWorkflow(true)}
              className="glass-button-secondary text-xs inline-flex items-center gap-1.5"
              title="Add the tasks from a saved event workflow"
            >
              <Workflow className="w-3.5 h-3.5" />
              Use a workflow
            </button>
          )}
          {perms.canCreate && (
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button text-xs inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Add task
            </button>
          )}
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setStatusFilter('')}
          className={cn(
            'text-[11px] px-2 py-0.5 rounded-full border transition-colors',
            statusFilter === ''
              ? 'bg-accent-500/15 text-accent-300 border-accent-500/25'
              : 'text-glass-400 border-glass-200/20 hover:text-glass-200',
          )}
        >
          All
        </button>
        {EVENT_TASK_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'text-[11px] px-2 py-0.5 rounded-full border transition-colors capitalize',
              statusFilter === s
                ? 'bg-accent-500/15 text-accent-300 border-accent-500/25'
                : 'text-glass-400 border-glass-200/20 hover:text-glass-200',
            )}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {showCreate && (
        <div className="glass-card p-3 space-y-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Task title"
            aria-label="Task title"
            className="glass-input w-full text-sm"
            autoFocus
          />
          <details className="subcard p-3">
            <summary className="text-sm text-glass-300 cursor-pointer min-h-[32px]">Details, assignee and due date (optional)</summary>
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            aria-label="Task description"
            className="glass-input w-full text-sm min-h-[50px]"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={newAssignee}
              aria-label="Task assignee"
              onChange={(e) => setNewAssignee(e.target.value)}
              className="glass-input text-sm flex-1 min-w-[160px]"
            >
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.minecraft_uuid} value={m.minecraft_uuid}>
                  {m.display_name}
                </option>
              ))}
            </select>
            <input
              type="datetime-local"
              value={newDue}
              onChange={(e) => setNewDue(e.target.value)}
              className="glass-input text-sm"
              title="Due date"
              aria-label="Task due date"
            />
          </div>
          </details>
          <div className="flex justify-end items-center gap-2">
            <button onClick={() => setShowCreate(false)} className="glass-button-secondary text-xs">
              Cancel
            </button>
            <button
              onClick={() => void handleCreate()}
              disabled={creating || !newTitle.trim()}
              className="glass-button text-xs inline-flex items-center gap-1.5"
            >
              {creating ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create
            </button>
          </div>
        </div>
      )}

      {tasks.length === 0 ? (
        <EmptyState bare compact icon={ClipboardList}
          title={statusFilter ? 'No tasks with this status' : 'Build the event run sheet'}
          description={statusFilter ? 'Choose another status or show all tasks.' : 'Add the first task, or use a workflow to start with a reusable checklist. Each task can have an owner and a due date.'}
          action={statusFilter ? <button className="glass-button-secondary" onClick={() => setStatusFilter('')}>Show all tasks</button> : !showCreate && perms.canCreate ? <button className="btn-primary" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" />Add the first task</button> : undefined}
        />
      ) : (
        <div className="space-y-1.5">
          {tasks.map((task, idx) => {
            const busy = busyId === task.id;
            const overdue =
              task.due_date != null && task.status !== 'done' && task.status !== 'skipped' && isPast(new Date(task.due_date));
            const isEditing = editingId === task.id;
            return (
              <div
                key={task.id}
                className={cn(
                  'rounded-lg border px-3 py-2',
                  task.status === 'done'
                    ? 'bg-green-500/[0.04] border-green-500/10'
                    : 'bg-glass-100/5 border-glass-200/10',
                )}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="glass-input w-full text-sm"
                    />
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description"
                      className="glass-input w-full text-sm min-h-[40px]"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={editAssignee}
                        onChange={(e) => setEditAssignee(e.target.value)}
                        className="glass-input text-sm flex-1 min-w-[140px]"
                      >
                        <option value="">Unassigned</option>
                        {members.map((m) => (
                          <option key={m.minecraft_uuid} value={m.minecraft_uuid}>
                            {m.display_name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="datetime-local"
                        value={editDue}
                        onChange={(e) => setEditDue(e.target.value)}
                        className="glass-input text-sm"
                      />
                      <button onClick={() => setEditingId(null)} className="glass-button-secondary !p-1.5">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => void handleSaveEdit(task)}
                        disabled={busy || !editTitle.trim()}
                        className="glass-button !p-1.5"
                      >
                        {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={cn(
                            'text-sm font-medium',
                            task.status === 'done' ? 'text-glass-400 line-through' : 'text-white',
                          )}
                        >
                          {task.title}
                        </span>
                        <EventTaskStatusBadge status={task.status} />
                        {task.source_workflow_id && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/25 inline-flex items-center gap-1"
                            title="Instantiated from a workflow"
                          >
                            <Workflow className="w-2.5 h-2.5" />
                            workflow
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-xs text-glass-400 mt-0.5 whitespace-pre-wrap">{task.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-glass-500">
                        {task.assignee_uuid && (
                          <PlayerProfile uuid={task.assignee_uuid} showUuid={false} inline />
                        )}
                        {task.due_date && (
                          <span className={overdue ? 'text-red-400 font-medium' : ''}>
                            Due {format(new Date(task.due_date), 'MMM d, HH:mm')}
                            {overdue && ' (overdue)'}
                          </span>
                        )}
                        {task.completed_at && (
                          <span className="text-green-400/70">
                            Done {format(new Date(task.completed_at), 'MMM d, HH:mm')}
                          </span>
                        )}
                      </div>
                    </div>

                    {perms.canCreate && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {statusFilter === '' && (
                          <div className="flex flex-col">
                            <button
                              onClick={() => void handleMove(task, -1)}
                              disabled={busy || idx === 0}
                              className="text-glass-500 hover:text-glass-200 disabled:opacity-30 p-0.5"
                              title="Move up"
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => void handleMove(task, 1)}
                              disabled={busy || idx === tasks.length - 1}
                              className="text-glass-500 hover:text-glass-200 disabled:opacity-30 p-0.5"
                              title="Move down"
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        <select
                          value={task.status}
                          onChange={(e) => void handleStatusChange(task, e.target.value as EventTaskStatus)}
                          disabled={busy}
                          className="glass-input text-[11px] py-1 capitalize"
                          title="Change status"
                        >
                          {EVENT_TASK_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s.replace(/_/g, ' ')}
                            </option>
                          ))}
                        </select>
                        {task.status !== 'done' && (
                          <button
                            onClick={() => void handleComplete(task)}
                            disabled={busy}
                            className="glass-button-secondary !p-1.5 text-green-300"
                            title="Complete task"
                          >
                            {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button
                          onClick={() => startEdit(task)}
                          disabled={busy}
                          className="glass-button-secondary !p-1.5"
                          title="Edit task"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => void handleDelete(task)}
                          disabled={busy}
                          className="glass-button-secondary !p-1.5 text-red-300"
                          title="Delete task"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ApplyWorkflowModal
        isOpen={showApplyWorkflow}
        eventId={eventId}
        scheduledStart={scheduledStart}
        onClose={() => setShowApplyWorkflow(false)}
        onSuccess={(result) => {
          void load();
          onChanged?.();
          alert(`${result.tasks_created} task${result.tasks_created === 1 ? '' : 's'} created.`);
        }}
      />
    </div>
  );
};
